import WolfCheckbox from '@components/ui/WolfCheckbox';
import WolfDropdown from '@components/ui/WolfDropdown';
import WolfInput from '@components/ui/WolfInput';
import WolfTooltip from '@components/ui/WolfTooltip';
import { useTranslation } from '@hooks/useTranslation';
import { CalendarRepetition } from '@models/integration.model';
import { InfoOutlined } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { DAYS_ARRAY, DAYS_NUMBER_ARRAY, MONTHS_ARRAY } from '@utils/constants';
import getRepeatRule, { translateRepeatRule } from '@utils/repeat-rule';
import { useEffect, useMemo, useState } from 'react';

interface CalendarRecurrenceProps {
  initialData: any;
  refreshRrule: (rrule: string) => void;
  timezone?: string;
  startDate?: string; // datetime-local format string
}

interface RecurrenceState {
  repeatType: CalendarRepetition;
  interval: number;
  weekDays: string[];
  monthlyType: 'date' | 'weekday';
  monthDay: number;
  weekdayPosition: number;
  weekdayName: 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';
  yearMonth: number; // 1-12 (January-December) for yearly recurrence
  yearDay: number; // 1-31 (day of month) for yearly recurrence
  endType: 'never' | 'after' | 'date';
  endCount: number | null;
  endDate: string | null;
  selectedPreset: string; // Track which preset is selected
  showCustom: boolean; // Control progressive disclosure
}

// CHECK: We may have to refactor this component once we do the new process edit flow
const CalendarRecurrence: React.FC<CalendarRecurrenceProps> = ({ initialData, refreshRrule, timezone, startDate }) => {
  const localeCalTrig = useTranslation('calTrig');
  const localeCommon = useTranslation('common');

  // Helper to get full day name (using translations)
  const getFullDayName = (shortName: string) => {
    const dayNames = (localeCalTrig as any)['dayNames'] || {};
    return dayNames[shortName] || shortName;
  };

  // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
  const getOrdinalSuffix = (num: number) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return `${num}st`;
    if (j === 2 && k !== 12) return `${num}nd`;
    if (j === 3 && k !== 13) return `${num}rd`;
    return `${num}th`;
  };

  // Helper function to get which occurrence of the weekday this is in the month (1-4 or -1 for 'last')
  const getWeekdayOccurrence = (date: Date) => {
    const dayOfMonth = date.getDate();
    const rawOccurrence = Math.ceil(dayOfMonth / 7);

    // Check if this is the last occurrence of this weekday in the month
    const nextWeek = new Date(date);
    nextWeek.setDate(dayOfMonth + 7);
    const isLastOccurrence = nextWeek.getMonth() !== date.getMonth();

    // If it's the last occurrence OR if it's the 5th occurrence, use -1 (last)
    // This ensures we never show "Fifth" - anything beyond 4th is "Last"
    const occurrence = isLastOccurrence || rawOccurrence >= 5 ? -1 : rawOccurrence;

    return { occurrence, isLastOccurrence };
  };

  // Smart preset options with intelligent defaults (recalculated when startDate changes)
  const getRepeatOptions = () => {
    // Use startDate if provided, otherwise use current date
    const referenceDate = startDate ? new Date(startDate) : new Date();
    const dayName = DAYS_ARRAY[referenceDate.getDay()];
    const fullDayName = getFullDayName(dayName);
    const dateNum = referenceDate.getDate();
    const { occurrence } = getWeekdayOccurrence(referenceDate);

    // Get ordinal name for occurrence (First, Second, Third, Fourth, or Last)
    // Note: We cap at 4, anything beyond should be "Last" (-1)
    const occurrenceNames: { [key: number]: string } = {
      1: localeCommon['first'],
      2: localeCommon['second'],
      3: localeCommon['third'],
      4: localeCommon['fourth'],
      [-1]: localeCommon['last'],
    };
    const occurrenceName = occurrenceNames[occurrence] || getOrdinalSuffix(occurrence);

    // Helper to format "on day X" pattern
    const onDayPattern = (localeCalTrig as any)['onDay'] || 'on day {day}';
    const onDayText = onDayPattern.replace('{day}', dateNum.toString());

    // Build the options array - only show monthly-first if occurrence is 1-4, not -1 (last)
    const options = [
      { text: localeCalTrig['noRepeat'], value: 'none', preset: true },
      { text: `${localeCalTrig['daily']} (${localeCalTrig['onlyWeekdays'] || localeCommon['weekdays'] || 'only weekdays'})`, value: 'weekdays', preset: true },
      { text: `${localeCalTrig['daily']} (${localeCalTrig['sevenDays'] || '7 days'})`, value: 'daily', preset: true },
      { text: `${localeCalTrig['weekly']} (${fullDayName})`, value: 'weekly', preset: true },
      { text: `${localeCalTrig['monthly']} (${onDayText})`, value: 'monthly', preset: true },
    ];

    // Only show the occurrence-based option if it's 1st-4th (not last)
    if (occurrence > 0 && occurrence <= 4) {
      options.push({ text: `${localeCalTrig['monthly']} (${occurrenceName} ${fullDayName})`, value: 'monthly-first', preset: true });
    }

    // Always show the "last" option
    options.push({ text: `${localeCalTrig['monthly']} (${localeCommon['last']} ${fullDayName})`, value: 'monthly-last', preset: true });
    options.push({ text: localeCalTrig['yearly'], value: 'yearly', preset: true });
    options.push({ text: `${localeCommon['custom']}...`, value: 'custom', preset: false });

    return options;
  };

  const REPEAT_OPTIONS = useMemo(() => getRepeatOptions(), [startDate, localeCalTrig, localeCommon]);

  const INTERVAL_OPTIONS: Record<CalendarRepetition, { text: string; value: string; }[]> = useMemo(() => ({
    [CalendarRepetition.NO_REPEAT]: [],
    [CalendarRepetition.DAILY]: Array.from({ length: 30 }, (_, i) => ({
      text: `${i + 1} ${i === 0 ? localeCalTrig['day'] : localeCalTrig['days']}`,
      value: (i + 1).toString()
    })),
    [CalendarRepetition.WEEKLY]: Array.from({ length: 20 }, (_, i) => ({
      text: `${i + 1} ${i === 0 ? localeCalTrig['week'] : localeCalTrig['weeks']}`,
      value: (i + 1).toString()
    })),
    [CalendarRepetition.MONTHLY]: Array.from({ length: 12 }, (_, i) => ({
      text: `${i + 1} ${i === 0 ? localeCalTrig['month'] : localeCalTrig['months']}`,
      value: (i + 1).toString()
    })),
    [CalendarRepetition.YEARLY]: Array.from({ length: 10 }, (_, i) => ({
      text: `${i + 1} ${i === 0 ? localeCalTrig['year'] : localeCalTrig['years']}`,
      value: (i + 1).toString()
    }))
  }), [localeCalTrig]);

  const END_OPTIONS = useMemo(() => [
    { text: localeCalTrig['indefinitely'], value: 'never' },
    { text: localeCalTrig['afterTimes'], value: 'after' },
    { text: localeCalTrig['atDate'], value: 'date' },
  ], [localeCalTrig]);

  const WEEKDAY_POSITION_OPTIONS = useMemo(() => [
    { text: localeCommon['first'], value: 1 },
    { text: localeCommon['second'], value: 2 },
    { text: localeCommon['third'], value: 3 },
    { text: localeCommon['fourth'], value: 4 },
    { text: localeCommon['last'], value: -1 },
  ], [localeCommon]);

  // Unified state for cleaner management
  const [recurrence, setRecurrence] = useState<RecurrenceState>({
    repeatType: CalendarRepetition.NO_REPEAT,
    interval: 1,
    weekDays: [],
    monthlyType: 'weekday',
    monthDay: 1,
    weekdayPosition: 1,
    weekdayName: 'MO',
    yearMonth: 1,
    yearDay: 1,
    endType: 'never',
    endCount: null,
    endDate: null,
    selectedPreset: 'none',
    showCustom: false,
  });

  // Helper to determine preset from existing data
  const determinePresetFromData = (initial: any) => {
    if (!initial || initial.repeatType === CalendarRepetition.NO_REPEAT) {
      return 'none';
    }

    // Check for common presets
    const interval = initial.repeatEvery || 1;
    const weekdays = initial.selectedDays || [];
    const isWeekdays = weekdays.length === 5 &&
      ['MO', 'TU', 'WE', 'TH', 'FR'].every(day => weekdays.includes(day));

    if (initial.repeatType === CalendarRepetition.DAILY && interval === 1 &&
        initial.endType === 'never') {
      return 'daily';
    }
    if (initial.repeatType === CalendarRepetition.WEEKLY && interval === 1 &&
        weekdays.length === 1 && initial.endType === 'never') {
      return 'weekly';
    }
    if (initial.repeatType === CalendarRepetition.WEEKLY && interval === 1 &&
        isWeekdays && initial.endType === 'never') {
      return 'weekdays';
    }
    if (initial.repeatType === CalendarRepetition.MONTHLY && interval === 1 &&
        initial.endType === 'never') {
      return 'monthly';
    }
    if (initial.repeatType === CalendarRepetition.YEARLY && interval === 1 &&
        initial.endType === 'never') {
      return 'yearly';
    }

    // If it doesn't match a preset, use custom
    return 'custom';
  };

  useEffect(() => {
    if (initialData) {
      const initial = translateRepeatRule(initialData);
      if (initial) {
        const preset = determinePresetFromData(initial);
        setRecurrence(prev => ({
          ...prev,
          repeatType: initial.repeatType as CalendarRepetition || CalendarRepetition.NO_REPEAT,
          interval: initial.repeatEvery || 1,
          weekDays: initial.selectedDays || [],
          monthDay: initial.selectedDay && !isNaN(parseInt(initial.selectedDay)) ? parseInt(initial.selectedDay) : 1,
          weekdayPosition: initial.position ? parseInt(initial.position) : 1,
          weekdayName: (['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].includes(initial.selectedDay || '') ? initial.selectedDay : 'MO') as 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU',
          yearMonth: initial.selectedMonth || 1,
          yearDay: initial.selectedDay && !isNaN(parseInt(initial.selectedDay)) ? parseInt(initial.selectedDay) : 1,
          endType: initial.iterations ? 'after' : (initial.endDate ? 'date' : 'never'),
          endCount: initial.iterations || null,
          endDate: initial.endDate ? (() => {
            const date = new Date(initial.endDate);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          })() : null,
          selectedPreset: preset,
          showCustom: preset === 'custom',
        }));
      }
    } else {
      setRecurrence({
        repeatType: CalendarRepetition.NO_REPEAT,
        interval: 1,
        weekDays: [],
        monthlyType: 'weekday',
        monthDay: 1,
        weekdayPosition: 1,
        weekdayName: 'MO',
        yearMonth: 1,
        yearDay: 1,
        endType: 'never',
        endCount: null,
        endDate: null,
        selectedPreset: 'none',
        showCustom: false,
      });
    }
  }, [initialData]);

  // Update recurrence state when startDate changes (to keep human-readable description in sync)
  useEffect(() => {
    if (!startDate || recurrence.repeatType === CalendarRepetition.NO_REPEAT) return;

    const referenceDate = new Date(startDate);
    const currentDayOfWeek = DAYS_ARRAY[referenceDate.getDay()];
    const currentDate = referenceDate.getDate();
    const { occurrence } = getWeekdayOccurrence(referenceDate);

    // Only update if using a preset (not custom)
    if (!recurrence.showCustom) {
      setRecurrence(prev => {
        const updates: Partial<RecurrenceState> = {};

        // Update weekly presets
        if (prev.selectedPreset === 'weekly' && prev.repeatType === CalendarRepetition.WEEKLY) {
          updates.weekDays = [currentDayOfWeek];
        }

        // Update monthly date preset
        if (prev.selectedPreset === 'monthly' && prev.repeatType === CalendarRepetition.MONTHLY && prev.monthlyType === 'date') {
          updates.monthDay = currentDate;
        }

        // Update monthly weekday preset (first/second/third/fourth occurrence)
        if (prev.selectedPreset === 'monthly-first' && prev.repeatType === CalendarRepetition.MONTHLY && prev.monthlyType === 'weekday') {
          // If occurrence is -1 (last), switch to monthly-last preset
          if (occurrence === -1) {
            updates.selectedPreset = 'monthly-last';
            updates.weekdayPosition = -1;
            updates.weekdayName = currentDayOfWeek as 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';
          } else {
            updates.weekdayPosition = occurrence;
            updates.weekdayName = currentDayOfWeek as 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';
          }
        }

        // Update monthly last weekday preset
        if (prev.selectedPreset === 'monthly-last' && prev.repeatType === CalendarRepetition.MONTHLY && prev.monthlyType === 'weekday') {
          // If occurrence is not -1 (1-4), we could switch to monthly-first, but let's keep it as last
          // Users can manually change if they want a specific occurrence
          updates.weekdayName = currentDayOfWeek as 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';
        }

        // Yearly preset: yearMonth and yearDay are derived from startDate, not stored in state

        return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
      });
    }
  }, [startDate]);

  useEffect(() => {
    if (recurrence.repeatType === CalendarRepetition.NO_REPEAT) return;

    const updateRepeatRule = () => {
      let iterations;
      let endDate;

      if (recurrence.endType === 'after' && recurrence.endCount && recurrence.endCount > 0) {
        iterations = recurrence.endCount;
      } else if (recurrence.endType === 'date' && recurrence.endDate) {
        endDate = recurrence.endDate;
      }

      // For monthly weekday type, pass only the day name (position is passed separately)
      let selectedDayForRule = recurrence.monthDay.toString();
      let position: string | null = null;
      let selectedMonth: number | undefined = undefined;

      if (recurrence.repeatType === CalendarRepetition.MONTHLY && recurrence.monthlyType === 'weekday') {
        selectedDayForRule = recurrence.weekdayName;
        position = recurrence.weekdayPosition.toString();
      }

      // For yearly recurrence, derive month and day from startDate
      if (recurrence.repeatType === CalendarRepetition.YEARLY && startDate) {
        const referenceDate = new Date(startDate);
        selectedMonth = referenceDate.getMonth() + 1; // 1=Jan, 12=Dec
        selectedDayForRule = referenceDate.getDate().toString();
        position = null; // Date pattern only, no weekday position
      }

      const rrule = getRepeatRule(
        recurrence.repeatType,
        recurrence.interval,
        recurrence.weekDays,
        selectedDayForRule,
        selectedMonth,
        position,
        iterations,
        endDate,
        timezone,
      );
      refreshRrule(rrule || '');
    };

    updateRepeatRule();
  }, [recurrence, timezone, refreshRrule]);

  // Smart preset handler with intelligent defaults
  const handlePresetChange = (item: any) => {
    const preset = item.value;
    // Use startDate if provided, otherwise use current date (same as preset labels)
    const referenceDate = startDate ? new Date(startDate) : new Date();
    const currentDayOfWeek = DAYS_ARRAY[referenceDate.getDay()];
    const currentDate = referenceDate.getDate();

    if (preset === 'custom') {
      // Show custom controls, set default repeatType if currently NO_REPEAT
      setRecurrence(prev => ({
        ...prev,
        selectedPreset: preset,
        showCustom: true,
        // If no repeat is set, default to daily when going to custom
        repeatType: prev.repeatType === CalendarRepetition.NO_REPEAT ? CalendarRepetition.DAILY : prev.repeatType,
      }));
      return;
    }

    // Apply intelligent defaults for each preset
    let newState: Partial<RecurrenceState> = {
      selectedPreset: preset,
      showCustom: false,
      interval: 1,
      endType: 'never',
      endCount: null,
      endDate: null,
    };

    switch (preset) {
      case 'none':
        newState.repeatType = CalendarRepetition.NO_REPEAT;
        break;
      case 'daily':
        newState.repeatType = CalendarRepetition.DAILY;
        break;
      case 'weekly':
        newState.repeatType = CalendarRepetition.WEEKLY;
        newState.weekDays = [currentDayOfWeek];
        break;
      case 'monthly':
        newState.repeatType = CalendarRepetition.MONTHLY;
        newState.monthlyType = 'date';
        newState.monthDay = currentDate;
        break;
      case 'monthly-first': {
        const { occurrence } = getWeekdayOccurrence(referenceDate);
        newState.repeatType = CalendarRepetition.MONTHLY;
        newState.monthlyType = 'weekday';
        newState.weekdayPosition = occurrence;
        newState.weekdayName = currentDayOfWeek as 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';
        break;
      }
      case 'monthly-last':
        newState.repeatType = CalendarRepetition.MONTHLY;
        newState.monthlyType = 'weekday';
        newState.weekdayPosition = -1; // -1 represents last occurrence
        newState.weekdayName = currentDayOfWeek as 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';
        break;
      case 'yearly':
        newState.repeatType = CalendarRepetition.YEARLY;
        // yearMonth and yearDay are derived from startDate, not stored in state
        break;
      case 'weekdays':
        newState.repeatType = CalendarRepetition.WEEKLY;
        newState.weekDays = ['MO', 'TU', 'WE', 'TH', 'FR'];
        break;
    }

    setRecurrence(prev => ({ ...prev, ...newState }));
  };

  // Legacy handler for custom controls (when in custom mode)
  const handleRepeatTypeChange = (item: any) => {
    const referenceDate = startDate ? new Date(startDate) : new Date();
    const currentDayOfWeek = DAYS_ARRAY[referenceDate.getDay()];

    setRecurrence(prev => ({
      ...prev,
      repeatType: item.value,
      // For weekly, default to current day to avoid empty selection
      weekDays: item.value === CalendarRepetition.WEEKLY ? [currentDayOfWeek] : [],
      interval: 1, // Reset to default
      endType: 'never', // Reset end conditions
      endCount: null,
      endDate: null,
    }));
  };

  const handleIntervalChange = (item: any) => {
    setRecurrence(prev => ({
      ...prev,
      interval: parseInt(item.value),
    }));
  };

  const handleWeekDayToggle = (day: string) => {
    setRecurrence(prev => {
      const isCurrentlySelected = prev.weekDays.includes(day);
      const newWeekDays = isCurrentlySelected
        ? prev.weekDays.filter(d => d !== day)
        : [...prev.weekDays, day];

      // Prevent removing the last day (must have at least one selected for weekly)
      if (newWeekDays.length === 0) {
        return prev; // Don't update if it would result in no days selected
      }

      return {
        ...prev,
        weekDays: newWeekDays,
      };
    });
  };

  const handleMonthlyTypeChange = (type: 'date' | 'weekday') => {
    setRecurrence(prev => ({
      ...prev,
      monthlyType: type,
      monthDay: type === 'date' ? 1 : prev.monthDay,
      weekdayPosition: type === 'weekday' ? 1 : prev.weekdayPosition,
      weekdayName: type === 'weekday' ? 'MO' : prev.weekdayName,
    }));
  };

  const handleMonthDayChange = (item: any) => {
    setRecurrence(prev => ({
      ...prev,
      monthDay: parseInt(item.value),
    }));
  };

  const handleWeekdayPositionChange = (item: any) => {
    setRecurrence(prev => ({
      ...prev,
      weekdayPosition: parseInt(item.value),
    }));
  };

  const handleWeekdayNameChange = (item: any) => {
    setRecurrence(prev => ({
      ...prev,
      weekdayName: item.value,
    }));
  };

  const handleEndTypeChange = (item: any) => {
    setRecurrence(prev => ({
      ...prev,
      endType: item.value,
      endCount: item.value === 'after' ? 1 : null,
      endDate: item.value === 'date' ? new Date().toISOString().split('T')[0] : null,
    }));
  };

  const handleEndCountChange = (event: any) => {
    const value = parseInt(event.target.value);
    setRecurrence(prev => ({
      ...prev,
      endCount: value > 0 ? value : 1,
    }));
  };

  const handleEndDateChange = (event: any) => {
    const selectedDate = new Date(event.target.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    setRecurrence(prev => ({
      ...prev,
      endDate: selectedDate >= today ? event.target.value : today.toISOString().split('T')[0],
    }));
  };


  // Helper to get current preset label (recalculate to reflect current startDate)
  const getCurrentPresetLabel = () => {
    const options = getRepeatOptions();
    const preset = options.find(option => option.value === recurrence.selectedPreset);
    return preset?.text || localeCalTrig['noRepeat'];
  };

  // Generate human-readable description of current recurrence settings
  const getHumanReadableDescription = () => {
    if (recurrence.repeatType === CalendarRepetition.NO_REPEAT) {
      return localeCalTrig['noRepeat'];
    }

    const recurrencePatterns = (localeCalTrig as any)['recurrencePatterns'] || {};
    const positionNamesTranslations = (localeCalTrig as any)['positionNames'] || {};
    const monthNamesTranslations = (localeCalTrig as any)['monthNames'] || {};

    const formatPattern = (pattern: string, replacements: { [key: string]: string | number }): string => {
      let result = pattern;
      for (const [key, value] of Object.entries(replacements)) {
        result = result.replace(`{${key}}`, String(value));
      }
      return result;
    };

    let description = '';

    switch (recurrence.repeatType) {
      case CalendarRepetition.DAILY:
        description = formatPattern(
          recurrence.interval > 1 ? recurrencePatterns.everyDays : recurrencePatterns.everyDay,
          { interval: recurrence.interval === 1 ? '' : recurrence.interval }
        );
        break;
      case CalendarRepetition.WEEKLY:
        if (recurrence.weekDays.length > 0) {
          const dayNames = recurrence.weekDays.map(day => getFullDayName(day)).join(', ');
          description = formatPattern(
            recurrence.interval > 1 ? recurrencePatterns.everyWeeksOn : recurrencePatterns.everyWeekOn,
            { interval: recurrence.interval === 1 ? '' : recurrence.interval, days: dayNames }
          );
        } else {
          description = formatPattern(
            recurrence.interval > 1 ? recurrencePatterns.everyWeeksOn : recurrencePatterns.everyWeekOn,
            { interval: recurrence.interval === 1 ? '' : recurrence.interval, days: '' }
          );
        }
        break;
      case CalendarRepetition.MONTHLY:
        if (recurrence.monthlyType === 'date') {
          description = formatPattern(
            recurrence.interval > 1 ? recurrencePatterns.everyMonthsOnDay : recurrencePatterns.everyMonthOnDay,
            { interval: recurrence.interval === 1 ? '' : recurrence.interval, day: recurrence.monthDay }
          );
        } else {
          const positionName = positionNamesTranslations[recurrence.weekdayPosition.toString()] ||
            `${recurrence.weekdayPosition}${getOrdinalSuffix(recurrence.weekdayPosition)}`;
          const dayName = getFullDayName(recurrence.weekdayName);
          description = formatPattern(
            recurrence.interval > 1 ? recurrencePatterns.everyMonthsOnThe : recurrencePatterns.everyMonthOnThe,
            { interval: recurrence.interval === 1 ? '' : recurrence.interval, position: positionName, day: dayName }
          );
        }
        break;
      case CalendarRepetition.YEARLY:
        if (startDate) {
          const referenceDate = new Date(startDate);
          const monthNum = referenceDate.getMonth() + 1;
          const monthName = monthNamesTranslations[monthNum.toString()] || MONTHS_ARRAY.find(m => m.value === monthNum)?.text || '';
          const yearDay = referenceDate.getDate();
          description = formatPattern(
            recurrence.interval > 1 ? recurrencePatterns.everyYearsOn : recurrencePatterns.everyYearOn,
            { interval: recurrence.interval === 1 ? '' : recurrence.interval, month: monthName, day: yearDay }
          );
        }
        break;
    }

    // Add end condition
    if (recurrence.endType === 'after' && recurrence.endCount) {
      description += `, ${recurrence.endCount} ${localeCalTrig['times']}`;
    } else if (recurrence.endType === 'date' && recurrence.endDate) {
      const date = new Date(recurrence.endDate);
      description += `, ${localeCalTrig['until'] || 'until'} ${date.toLocaleDateString()}`;
    }

    return description;
  };

  return (
    <Box className="flex flex-col gap-6">
      {/* Smart Preset Dropdown - Single Entry Point */}
      <Box>
        <Box className="flex items-center gap-1 mb-2">
          <Typography variant="body14semibold">
            {localeCalTrig['repeat']}
          </Typography>
          <WolfTooltip title={localeCalTrig['repeatTooltip']}>
            <IconButton size="small">
              <InfoOutlined sx={{ width: 18, height: 18 }} className="text-primaryDark" />
            </IconButton>
          </WolfTooltip>
        </Box>
        <WolfDropdown
          label={getCurrentPresetLabel()}
          showBorder={true}
          singleSelect={true}
          items={REPEAT_OPTIONS}
          initialSelectedItems={[recurrence.selectedPreset]}
          onChange={(item) => handlePresetChange(item[0])}
        />
        {/* Human-readable description */}
        {recurrence.repeatType !== CalendarRepetition.NO_REPEAT && (
          <Box className="mt-4">
            <Typography variant="body14semibold" className="mb-1">
              {localeCalTrig['currentSelection']} 
            </Typography>
            <Typography variant="body14" className="text-gray-600 italic">
              {getHumanReadableDescription()}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Progressive disclosure - only show when Custom is selected */}
      {recurrence.showCustom && (
        <>
          {/* Custom Type Selection */}
          <Box>
            <Typography variant="body14semibold" className="mb-2">
              {localeCalTrig['repeatPattern']}
            </Typography>
            <WolfDropdown
              label={[
                { text: localeCalTrig['daily'], value: CalendarRepetition.DAILY },
                { text: localeCalTrig['weekly'], value: CalendarRepetition.WEEKLY },
                { text: localeCalTrig['monthly'], value: CalendarRepetition.MONTHLY },
                { text: localeCalTrig['yearly'], value: CalendarRepetition.YEARLY },
              ].find(option => option.value === recurrence.repeatType)?.text || localeCalTrig['daily']}
              showBorder={true}
              singleSelect={true}
              items={[
                { text: localeCalTrig['daily'], value: CalendarRepetition.DAILY },
                { text: localeCalTrig['weekly'], value: CalendarRepetition.WEEKLY },
                { text: localeCalTrig['monthly'], value: CalendarRepetition.MONTHLY },
                { text: localeCalTrig['yearly'], value: CalendarRepetition.YEARLY },
              ]}
              initialSelectedItems={[recurrence.repeatType]}
              onChange={(item) => handleRepeatTypeChange(item[0])}
            />
          </Box>

          {/* Interval Selection (Every X days/weeks/months) */}
          <Box className="flex items-center gap-4">
            <Typography variant="body14semibold">{localeCalTrig['every']}</Typography>
            <Box className="flex-1">
              <WolfDropdown
                label={INTERVAL_OPTIONS[recurrence.repeatType]?.find(option => option.value === recurrence.interval.toString())?.text || '1'}
                showBorder={true}
                singleSelect={true}
                items={INTERVAL_OPTIONS[recurrence.repeatType] || []}
                initialSelectedItems={[recurrence.interval.toString()]}
                onChange={(item) => handleIntervalChange(item[0])}
              />
            </Box>
          </Box>

          {/* Weekly: Day Selection */}
          {recurrence.repeatType === CalendarRepetition.WEEKLY && (
            <Box>
              <Typography variant="body14semibold" className="mb-2">
                {localeCalTrig['onDays']}
              </Typography>
              <Box className="flex flex-wrap gap-2 justify-between">
                {DAYS_ARRAY.map((day) => (
                  <WolfCheckbox
                    key={day}
                    checked={recurrence.weekDays.includes(day)}
                    onChange={() => handleWeekDayToggle(day)}
                    label={getFullDayName(day)}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Monthly: Date/Weekday Selection */}
          {recurrence.repeatType === CalendarRepetition.MONTHLY && (
            <Box>
              <Typography variant="body14semibold" className="mb-2">
                {localeCalTrig['onThe']}
              </Typography>
              <Box className="flex gap-4 items-center mb-4">
                <Box className="flex gap-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="monthlyType"
                      checked={recurrence.monthlyType === 'date'}
                      onChange={() => handleMonthlyTypeChange('date')}
                      className="mr-2"
                    />
                    <span>{localeCalTrig['dayLabel']}</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="monthlyType"
                      checked={recurrence.monthlyType === 'weekday'}
                      onChange={() => handleMonthlyTypeChange('weekday')}
                      className="mr-2"
                    />
                    <span>{localeCalTrig['weekdayLabel']}</span>
                  </label>
                </Box>
              </Box>

              {recurrence.monthlyType === 'date' ? (
                <Box className="w-1/2">
                  <WolfDropdown
                    label={`${recurrence.monthDay}`}
                    showBorder={true}
                    singleSelect={true}
                    items={DAYS_NUMBER_ARRAY.map((day) => ({
                      text: day.toString(),
                      value: day.toString(),
                    }))}
                    initialSelectedItems={[recurrence.monthDay.toString()]}
                    onChange={(item) => handleMonthDayChange(item[0])}
                  />
                </Box>
              ) : (
                <Box className="flex gap-4">
                  <WolfDropdown
                    label={WEEKDAY_POSITION_OPTIONS.find(option => option.value === recurrence.weekdayPosition)?.text || localeCommon['first']}
                    showBorder={true}
                    singleSelect={true}
                    items={WEEKDAY_POSITION_OPTIONS.map(option => ({ text: option.text, value: option.value.toString() }))}
                    initialSelectedItems={[recurrence.weekdayPosition.toString()]}
                    onChange={(item) => handleWeekdayPositionChange(item[0])}
                  />
                  <WolfDropdown
                    label={getFullDayName(recurrence.weekdayName)}
                    showBorder={true}
                    singleSelect={true}
                    items={DAYS_ARRAY.map((day) => ({
                      text: getFullDayName(day),
                      value: day,
                    }))}
                    initialSelectedItems={[recurrence.weekdayName]}
                    onChange={(item) => handleWeekdayNameChange(item[0])}
                  />
                </Box>
              )}
            </Box>
          )}


          {/* End Conditions */}
          <Box>
            <Typography variant="body14semibold" className="mb-2">
              {localeCalTrig['repeatUntil']}
            </Typography>
            <Box className="flex gap-4 items-end">
              <Box className="w-1/2">
                <WolfDropdown
                  label={END_OPTIONS.find(option => option.value === recurrence.endType)?.text || localeCalTrig['indefinitely']}
                  showBorder={true}
                  singleSelect={true}
                  items={END_OPTIONS}
                  initialSelectedItems={[recurrence.endType]}
                  onChange={(item) => handleEndTypeChange(item[0])}
                />
              </Box>
              {recurrence.endType === 'after' && (
                <Box className="w-1/2">
                  <WolfInput
                    fullWidth
                    type="number"
                    label={localeCalTrig['times']}
                    value={recurrence.endCount || 1}
                    onChange={handleEndCountChange}
                  />
                </Box>
              )}
              {recurrence.endType === 'date' && (
                <Box className="w-1/2">
                  <WolfInput
                    fullWidth
                    type="date"
                    label={localeCalTrig['date']}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={recurrence.endDate || ''}
                    onChange={handleEndDateChange}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CalendarRecurrence;
